import { Megaphone, Pencil, PenLine, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import {
  createOrganizationNotice,
  deleteOrganizationNotice,
  getOrganizationNoticeDetail,
  getOrganizationNotices,
  updateOrganizationNotice,
} from "lib/api/organizationNotices";
import { formatKoreanDateTime } from "lib/utils/koreanDateTime";

function formatNoticeDate(value) {
  return formatKoreanDateTime(value, {
    sourceTimeZone: "kst",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function OrganizationNoticeDetail({
  actions = null,
  item,
  loading = false,
  error = "",
  emptyText = "목록에서 공지사항을 선택해 주세요.",
}) {
  if (loading) {
    return (
      <div className="organizationNotice__detailState">
        공지사항을 불러오는 중입니다.
      </div>
    );
  }

  if (error) {
    return <div className="organizationNotice__detailState isError">{error}</div>;
  }

  if (!item) {
    return <div className="organizationNotice__detailState">{emptyText}</div>;
  }

  return (
    <div className="organizationNotice__detail">
      <header>
        <h3>{item.title}</h3>
        <div className="organizationNotice__meta">
          <span>{item.author_name || "-"}</span>
          <span className="organizationNotice__createdAt">
            {formatNoticeDate(item.created_at)}
          </span>
        </div>
      </header>
      {actions && (
        <div className="organizationNotice__detailActions">{actions}</div>
      )}
      <div className="organizationNotice__body">{item.body}</div>
    </div>
  );
}

export function OrganizationLatestNotice({ organizationId }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;

    async function loadLatestNotice() {
      if (!organizationId) {
        setItem(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      const listResult = await getOrganizationNotices({
        organizationId,
        limit: 1,
      });
      if (!alive) return;

      const latest = listResult.data?.items?.[0] ?? null;
      if (!listResult.success) {
        setError(listResult.message);
        setItem(null);
        setLoading(false);
        return;
      }

      if (!latest?.id) {
        setItem(null);
        setLoading(false);
        return;
      }

      const detailResult = await getOrganizationNoticeDetail(latest.id);
      if (!alive) return;

      setItem(detailResult.success ? detailResult.data?.item ?? null : null);
      setError(detailResult.success ? "" : detailResult.message);
      setLoading(false);
    }

    loadLatestNotice();
    return () => {
      alive = false;
    };
  }, [organizationId]);

  return (
    <article className="organizationPage__panel organizationPage__panel--full organizationNotice__latest">
      <div className="organizationPage__panelHeader">
        <h3>최근 공지사항</h3>
      </div>
      <OrganizationNoticeDetail
        emptyText="아직 작성된 공지사항이 없습니다."
        error={error}
        item={item}
        loading={loading}
      />
    </article>
  );
}

export default function OrganizationNotices({
  organizationId,
  notify,
}) {
  const [items, setItems] = useState([]);
  const [selectedNoticeId, setSelectedNoticeId] = useState("");
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [listError, setListError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [canWrite, setCanWrite] = useState(false);
  const [isWriting, setIsWriting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");

  const loadList = useCallback(
    async (preferredNoticeId = "") => {
      if (!organizationId) {
        setItems([]);
        setSelectedNoticeId("");
        setCanWrite(false);
        setLoadingList(false);
        return;
      }

      setLoadingList(true);
      setCanWrite(false);
      setListError("");
      const result = await getOrganizationNotices({ organizationId });
      setLoadingList(false);

      if (!result.success) {
        setItems([]);
        setSelectedNoticeId("");
        setCanWrite(false);
        setListError(result.message);
        return;
      }

      const nextItems = Array.isArray(result.data?.items)
        ? result.data.items
        : [];
      setCanWrite(result.data?.can_write === true);
      setItems(nextItems);
      setSelectedNoticeId((current) => {
        const requestedId = preferredNoticeId || current;
        return nextItems.some((item) => item.id === requestedId)
          ? requestedId
          : nextItems[0]?.id || "";
      });
    },
    [organizationId],
  );

  useEffect(() => {
    setSelectedNotice(null);
    setSelectedNoticeId("");
    setIsWriting(false);
    setIsEditing(false);
    setIsDeleteConfirmOpen(false);
    setTitle("");
    setBody("");
    setEditTitle("");
    setEditBody("");
    loadList();
  }, [loadList]);

  useEffect(() => {
    let alive = true;

    async function loadDetail() {
      setIsEditing(false);
      setIsDeleteConfirmOpen(false);

      if (!selectedNoticeId) {
        setSelectedNotice(null);
        setDetailError("");
        setLoadingDetail(false);
        return;
      }

      setLoadingDetail(true);
      setDetailError("");
      const result = await getOrganizationNoticeDetail(selectedNoticeId);
      if (!alive) return;

      setSelectedNotice(result.success ? result.data?.item ?? null : null);
      setDetailError(result.success ? "" : result.message);
      setLoadingDetail(false);
    }

    loadDetail();
    return () => {
      alive = false;
    };
  }, [selectedNoticeId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    if (!trimmedTitle || !trimmedBody) {
      notify?.("제목과 내용을 모두 입력해 주세요.", false);
      return;
    }

    setIsSubmitting(true);
    const result = await createOrganizationNotice({
      organizationId,
      title: trimmedTitle,
      body: trimmedBody,
    });
    setIsSubmitting(false);

    if (!result.success) {
      notify?.(result.message, false);
      return;
    }

    const createdNoticeId = result.data?.item?.id || "";
    setTitle("");
    setBody("");
    setIsWriting(false);
    notify?.("공지사항을 작성했습니다.", true);
    await loadList(createdNoticeId);
  };

  const handleStartEdit = () => {
    if (!selectedNotice || !canWrite) return;

    setIsWriting(false);
    setEditTitle(selectedNotice.title ?? "");
    setEditBody(selectedNotice.body ?? "");
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (isUpdating) return;
    setIsEditing(false);
    setEditTitle("");
    setEditBody("");
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!selectedNotice?.id || isUpdating) return;

    const trimmedTitle = editTitle.trim();
    const trimmedBody = editBody.trim();
    if (!trimmedTitle || !trimmedBody) {
      notify?.("제목과 내용을 모두 입력해 주세요.", false);
      return;
    }

    setIsUpdating(true);
    const result = await updateOrganizationNotice({
      noticeId: selectedNotice.id,
      title: trimmedTitle,
      body: trimmedBody,
    });
    setIsUpdating(false);

    if (!result.success) {
      notify?.(result.message, false);
      return;
    }

    const updatedItem = result.data?.item ?? {
      ...selectedNotice,
      title: trimmedTitle,
      body: trimmedBody,
    };
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.id === updatedItem.id
          ? {
              ...item,
              ...updatedItem,
            }
          : item,
      ),
    );
    setSelectedNotice((currentItem) =>
      currentItem?.id === updatedItem.id ? updatedItem : currentItem,
    );
    setIsEditing(false);
    setEditTitle("");
    setEditBody("");
    notify?.("공지사항을 수정했습니다.", true);
  };

  const handleDelete = async () => {
    if (!selectedNotice?.id || isDeleting) return;

    const deletedNoticeId = selectedNotice.id;
    setIsDeleting(true);
    const result = await deleteOrganizationNotice(deletedNoticeId);
    setIsDeleting(false);

    if (!result.success) {
      notify?.(result.message, false);
      return;
    }

    setIsDeleteConfirmOpen(false);
    setIsEditing(false);
    setSelectedNotice(null);
    setSelectedNoticeId("");
    notify?.("공지사항을 삭제했습니다.", true);
    await loadList();
  };

  return (
    <>
      <div className="organizationNotice">
      <section className="organizationPage__panel organizationNotice__listPanel">
        <div className="organizationPage__panelHeader">
          <div>
            <h3>공지사항</h3>
            <span>{items.length}건</span>
          </div>
          {canWrite && (
            <button
              className="organizationPage__createBtn"
              onClick={() => {
                setIsWriting((current) => !current);
                setIsEditing(false);
              }}
              type="button"
            >
              {isWriting ? <X size={15} /> : <PenLine size={15} />}
              <span>{isWriting ? "작성 취소" : "공지 작성"}</span>
            </button>
          )}
        </div>

        {isWriting && (
          <form className="organizationNotice__form" onSubmit={handleSubmit}>
            <label>
              <span>제목</span>
              <input
                maxLength={160}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="공지 제목을 입력해 주세요."
                type="text"
                value={title}
              />
            </label>
            <label>
              <span>내용</span>
              <textarea
                maxLength={20000}
                onChange={(event) => setBody(event.target.value)}
                placeholder="공지 내용을 입력해 주세요."
                value={body}
              />
            </label>
            <button
              className="organizationPage__createBtn"
              disabled={isSubmitting}
              type="submit"
            >
              <span>{isSubmitting ? "작성 중" : "작성"}</span>
            </button>
          </form>
        )}

        {listError && (
          <p className="organizationPage__panelNote isError">{listError}</p>
        )}

        <div className="organizationNotice__list">
          {loadingList ? (
            <div className="organizationNotice__listState">
              공지사항 목록을 불러오는 중입니다.
            </div>
          ) : items.length > 0 ? (
            items.map((item) => (
              <button
                className={item.id === selectedNoticeId ? "isActive" : ""}
                key={item.id}
                onClick={() => setSelectedNoticeId(item.id)}
                type="button"
              >
                <strong>{item.title}</strong>
                <span>
                  {item.author_name || "-"} · {formatNoticeDate(item.created_at)}
                </span>
              </button>
            ))
          ) : (
            <div className="organizationNotice__listState">
              <Megaphone size={28} aria-hidden="true" />
              <span>아직 작성된 공지사항이 없습니다.</span>
            </div>
          )}
        </div>
      </section>

      <section className="organizationPage__panel organizationNotice__detailPanel">
        {isEditing && selectedNotice ? (
          <form
            className="organizationNotice__form organizationNotice__editForm"
            onSubmit={handleUpdate}
          >
            <div className="organizationNotice__editHeader">
              <strong>공지사항 수정</strong>
              <button
                aria-label="공지 수정 취소"
                className="organizationPage__modalClose"
                disabled={isUpdating}
                onClick={handleCancelEdit}
                type="button"
              >
                <X aria-hidden="true" size={17} />
              </button>
            </div>
            <label>
              <span>제목</span>
              <input
                autoFocus
                maxLength={160}
                onChange={(event) => setEditTitle(event.target.value)}
                type="text"
                value={editTitle}
              />
            </label>
            <label>
              <span>내용</span>
              <textarea
                maxLength={20000}
                onChange={(event) => setEditBody(event.target.value)}
                value={editBody}
              />
            </label>
            <div className="organizationNotice__formActions">
              <button
                className="organizationPage__secondaryBtn"
                disabled={isUpdating}
                onClick={handleCancelEdit}
                type="button"
              >
                취소
              </button>
              <button
                className="organizationPage__saveBtn"
                disabled={isUpdating}
                type="submit"
              >
                <Pencil aria-hidden="true" size={15} />
                {isUpdating ? "수정 중" : "수정"}
              </button>
            </div>
          </form>
        ) : (
          <OrganizationNoticeDetail
            actions={
              canWrite && selectedNotice ? (
                <>
                  <button
                    className="organizationPage__secondaryBtn"
                    onClick={handleStartEdit}
                    type="button"
                  >
                    <Pencil aria-hidden="true" size={15} />
                    수정
                  </button>
                  <button
                    className="organizationPage__dangerBtn"
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    type="button"
                  >
                    <Trash2 aria-hidden="true" size={15} />
                    삭제
                  </button>
                </>
              ) : null
            }
            error={detailError}
            item={selectedNotice}
            loading={loadingDetail}
          />
        )}
      </section>
      </div>

      {isDeleteConfirmOpen && selectedNotice && (
        <div
          className="organizationPage__modalBackdrop"
          onMouseDown={() => {
            if (!isDeleting) setIsDeleteConfirmOpen(false);
          }}
          role="presentation"
        >
          <section
            aria-describedby="organization-notice-delete-description"
            aria-labelledby="organization-notice-delete-title"
            aria-modal="true"
            className="organizationPage__modal organizationPage__modal--confirm"
            onMouseDown={(event) => event.stopPropagation()}
            role="alertdialog"
          >
            <div className="organizationPage__modalHeader">
              <div>
                <h3 id="organization-notice-delete-title">공지사항 삭제</h3>
              </div>
              <button
                aria-label="공지 삭제 확인창 닫기"
                className="organizationPage__modalClose"
                disabled={isDeleting}
                onClick={() => setIsDeleteConfirmOpen(false)}
                type="button"
              >
                <X aria-hidden="true" size={17} />
              </button>
            </div>

            <div
              className="organizationPage__modalBody"
              id="organization-notice-delete-description"
            >
              <div className="organizationPage__confirmIntro">
                <span className="organizationPage__confirmIcon">
                  <Trash2 aria-hidden="true" size={20} />
                </span>
                <div className="organizationPage__confirmText">
                  <strong>선택한 공지사항을 삭제합니다.</strong>
                  <p>삭제한 공지사항은 복구할 수 없습니다.</p>
                </div>
              </div>
            </div>

            <div className="organizationPage__modalActions">
              <button
                autoFocus
                className="organizationPage__secondaryBtn"
                disabled={isDeleting}
                onClick={() => setIsDeleteConfirmOpen(false)}
                type="button"
              >
                취소
              </button>
              <button
                className="organizationPage__dangerBtn"
                disabled={isDeleting}
                onClick={handleDelete}
                type="button"
              >
                <Trash2 aria-hidden="true" size={15} />
                {isDeleting ? "삭제 중" : "삭제"}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
